// The talent's Request Spotlight settings (QR Code tab → Fan's Spotlight): on/off, the
// screen link, Style and Fun icons. Read when the talent's own QR Code tab
// opens (and kept for the session, so the button is there at once next time);
// every change shows at once and snaps back, with a reason, if the save fails.
import { useCallback, useEffect, useState } from "react";
import { spotlightApi } from "../services/spotlight";
import type { SpotlightSettings, SpotlightSettingsPatch } from "../types/spotlight";
import { pageCache } from "../lib/pageCache";

const CACHE_KEY = "spotlight-settings";

export function useSpotlightSettings(active: boolean) {
  const [settings, setSettings] = useState<SpotlightSettings | null>(() => pageCache.get<SpotlightSettings>(CACHE_KEY) ?? null);
  const [failed, setFailed] = useState(false);
  // First read of the session still on its way (the QR tab keeps the
  // button's place meanwhile, so the row doesn't re-flow when it lands).
  const [pending, setPending] = useState(() => !pageCache.get<SpotlightSettings>(CACHE_KEY));
  const [busy, setBusy] = useState(false);

  const keep = useCallback((s: SpotlightSettings) => {
    pageCache.set(CACHE_KEY, s);
    setSettings(s);
  }, []);

  useEffect(() => {
    if (!active) return;
    let gone = false;
    spotlightApi
      .settings()
      .then((s) => {
        if (!gone) keep(s);
      })
      .catch(() => {
        /* an older server (no Spotlight yet), or offline: no row, nothing broken */
      })
      .finally(() => {
        if (!gone) setPending(false);
      });
    return () => {
      gone = true;
    };
  }, [active, keep]);

  const save = useCallback(
    async (patch: SpotlightSettingsPatch) => {
      if (!settings) return;
      const before = settings;
      setFailed(false);
      setSettings({ ...settings, ...patch });
      try {
        keep(await spotlightApi.update(patch));
      } catch {
        setSettings(before);
        setFailed(true);
      }
    },
    [settings, keep],
  );

  const resetLink = useCallback(async () => {
    setFailed(false);
    setBusy(true);
    try {
      keep(await spotlightApi.resetLink());
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [keep]);

  return { settings, failed, busy, pending, save, resetLink };
}

export type SpotlightSettingsState = ReturnType<typeof useSpotlightSettings>;
